import { z } from 'zod';

type T = (key: string) => string;

export const createShelfSchema = (t: T) =>
  z.object({
    shelf_type: z
      .string()
      .min(1, t('shelfTypeRequired'))
      .max(50, t('shelfTypeMax')),
    slot: z.string().max(50, t('slotMax')).optional(),
    max_capacity: z
      .number()
      .int()
      .min(1, t('capacityMin'))
      .max(999, t('capacityMax'))
      .or(
        z
          .string()
          .transform((v) => parseInt(v))
          .pipe(
            z.number().int().min(1, t('capacityMin')).max(999, t('capacityMax'))
          )
      ),
    storage_location_id: z
      .number({ error: t('storageLocationRequired') })
      .int()
      .positive(t('storageLocationRequired'))
      .or(
        z
          .string()
          .min(1, t('storageLocationRequired'))
          .transform((v) => parseInt(v))
          .pipe(z.number().int().positive(t('storageLocationRequired')))
      )
  });

export const updateShelfSchema = (t: T) =>
  z.object({
    shelf_type: z.string().max(50, t('shelfTypeMax')).optional(),
    slot: z.string().max(50, t('slotMax')).optional(),
    max_capacity: z
      .number()
      .int()
      .min(1, t('capacityMin'))
      .max(999, t('capacityMax'))
      .or(
        z
          .string()
          .transform((v) => parseInt(v))
          .pipe(
            z.number().int().min(1, t('capacityMin')).max(999, t('capacityMax'))
          )
      )
      .optional(),
    status: z.enum(['active', 'full', 'maintenance']).optional(),
    storage_location_id: z
      .number()
      .int()
      .positive()
      .or(
        z
          .string()
          .transform((v) => parseInt(v))
          .pipe(z.number().int().positive())
      )
      .optional()
  });

// Default instances with English fallbacks (for use outside React)
const defaultFallbacks: Record<string, string> = {
  shelfTypeRequired: 'Shelf type is required',
  shelfTypeMax: 'Shelf type must not exceed 50 characters',
  slotRequired: 'ID is required',
  slotMax: 'ID must not exceed 50 characters',
  storageLocationRequired: 'Storage location is required',
  capacityMin: 'Capacity must be at least 1',
  capacityMax: 'Capacity must not exceed 999'
};

export const shelfSchema = createShelfSchema(
  (key) => defaultFallbacks[key] ?? key
);
export const shelfUpdateSchema = updateShelfSchema(
  (key) => defaultFallbacks[key] ?? key
);

export type ShelfFormValues = z.infer<typeof shelfSchema>;
export type ShelfUpdateFormValues = z.infer<typeof shelfUpdateSchema>;
